import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { MotionBox } from "@rusha/shared-theme";
import LayersIcon from "@mui/icons-material/Layers";
import LinkIcon from "@mui/icons-material/Link";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";

const MotionTypography = motion.create(Typography);

const steps = [
  {
    title: "Pick a Stack",
    description:
      "Choose from battle-tested templates. React + FastAPI, Next.js + NestJS, Airbyte + dbt + Trino — all pre-configured and ready.",
    icon: LayersIcon,
    color: "primary.main",
  },
  {
    title: "Connect the Dots",
    description:
      "Link your cloud account and GitHub org. We handle the credentials securely. No manual IAM role setup.",
    icon: LinkIcon,
    color: "secondary.main",
  },
  {
    title: "Hit Deploy",
    description:
      "One click. We provision the infrastructure, create the repo, install webhooks, and deploy. You get a live URL.",
    icon: RocketLaunchIcon,
    color: "success.main",
  },
  {
    title: "We Maintain",
    description:
      "Real-time monitoring, cost tracking, and scaling. When you push code, we redeploy automatically.",
    icon: PsychologyAltIcon,
    color: "warning.main",
  },
];

const HowItWorks: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      id="how-it-works"
      component="section"
      sx={{
        py: 20,
        backgroundColor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background texture */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 50%),
            radial-gradient(circle at 80% 50%, ${alpha(theme.palette.secondary.main, 0.04)} 0%, transparent 50%)
          `,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <MotionBox
          sx={{ textAlign: "center", mb: 12 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <MotionTypography
            variant="h2"
            sx={{
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            From Code to Live URL
          </MotionTypography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 550,
              mx: "auto",
              fontWeight: 400,
            }}
          >
            Four steps. That's it. No terraform, no kubectl, no YAML wrestling.
          </Typography>
        </MotionBox>

        <Grid container spacing={4}>
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <MotionBox
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  whileHover={{ y: -6 }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      p: 4,
                      borderRadius: 4,
                      background: "background.paper",
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: alpha(
                          theme.palette[
                            step.color.split(".")[0] as
                              | "primary"
                              | "secondary"
                              | "success"
                              | "warning"
                          ].main,
                          0.4,
                        ),
                        boxShadow: `0 20px 60px ${alpha(theme.palette[step.color.split(".")[0] as "primary" | "secondary" | "success" | "warning"].main, 0.12)}`,
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: 4,
                        background:
                          theme.palette[
                            step.color.split(".")[0] as
                              | "primary"
                              | "secondary"
                              | "success"
                              | "warning"
                          ].main,
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        background: alpha(
                          theme.palette[
                            step.color.split(".")[0] as
                              | "primary"
                              | "secondary"
                              | "success"
                              | "warning"
                          ].main,
                          0.1,
                        ),
                      }}
                    >
                      <IconComponent sx={{ fontSize: 24, color: step.color }} />
                    </Box>

                    {/* Step indicator */}
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: step.color,
                        letterSpacing: "0.15em",
                        mb: 1,
                        display: "block",
                      }}
                    >
                      STEP {String(index + 1).padStart(2, "0")}
                    </Typography>

                    {/* Title */}
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      {step.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                        fontSize: "0.9rem",
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </MotionBox>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default HowItWorks;
